import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, CircularProgress, Alert, Chip, Tooltip,
} from '@mui/material';
import { AddCircle, DeleteOutline, SwapHoriz, WarningAmber, Lock, CheckCircle } from '@mui/icons-material';
import {
  P, cardSx, fieldSx, priBtn, outBtn, gridInput, thSx, thTitle, PX,
  FieldLabel, WorkspaceHeader, TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import {
  materialConversionService, ConversionType, ConversionPreview,
  CONVERSION_TYPE_LABELS,
} from '../../services/materialConversionService';

/**
 * Material Conversion / Reclassification workspace.
 *
 * The screen exists because "change the product master" is not a conversion —
 * it rewrites history, moves no stock and posts nothing. Here, a conversion is
 * a transaction: source out, destination in, loss expensed, ledger updated.
 *
 * Two things it deliberately does NOT do:
 *  - it does not let the user compute the destination quantity. The server
 *    derives it from (source − loss) × the rule's conversion factor, and the
 *    result is displayed as they type.
 *  - it does not offer arbitrary conversions. Every line is checked against
 *    the configured rules BEFORE anything can be saved, and a line with no
 *    rule is shown as blocked with the reason.
 */

const todayISO = () => new Date().toISOString().slice(0, 10);
const toNum = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const REASON_CODES = [
  'GRADING', 'REPACKING', 'RECLASSIFICATION', 'PROCESS_OUTPUT',
  'QUALITY_DOWNGRADE', 'CUSTOMER_RETURN_REWORK', 'CORRECTION', 'OTHER',
];

let keySeq = 0;
const nextKey = () => `mc-${++keySeq}`;

interface LineDraft {
  key: string;
  sourceProduct: any | null;
  destProduct: any | null;
  sourceQuantity: string;
  lossQuantity: string;
  remarks: string;
}

const emptyLine = (): LineDraft => ({
  key: nextKey(), sourceProduct: null, destProduct: null,
  sourceQuantity: '', lossQuantity: '', remarks: '',
});

const MaterialConversionWorkspacePage: React.FC = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [loadingRef, setLoadingRef] = useState(true);

  const [conversionDate, setConversionDate] = useState(todayISO());
  const [conversionType, setConversionType] = useState<ConversionType>('PROCESS_CONVERSION');
  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [destWarehouseId, setDestWarehouseId] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);

  const [preview, setPreview] = useState<ConversionPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoadingRef(true);
    Promise.all([
      api.get<{ products: any[] }>('/products').catch(() => ({ products: [] })),
      api.get<{ warehouses: any[] }>('/inventory/warehouses').catch(() => ({ warehouses: [] })),
    ]).then(([p, w]: any) => {
      setProducts((p.products || []).filter((x: any) => x.is_active !== false));
      setWarehouses(w.warehouses || []);
    }).finally(() => setLoadingRef(false));
  }, []);

  const setLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const readyLines = useMemo(
    () => lines.filter((l) => l.sourceProduct && l.destProduct && toNum(l.sourceQuantity) > 0),
    [lines]
  );

  // The server does the arithmetic AND the authorisation check, debounced as
  // the user types — so a conversion nobody configured is visible as blocked
  // long before anyone presses save.
  const previewKey = useMemo(() => {
    if (!readyLines.length) return null;
    return JSON.stringify({
      conversionType,
      sourceWarehouseId: sourceWarehouseId ? Number(sourceWarehouseId) : null,
      lines: readyLines.map((l) => ({
        sourceProductId: Number(l.sourceProduct.id),
        destProductId: Number(l.destProduct.id),
        sourceQuantity: toNum(l.sourceQuantity),
        lossQuantity: toNum(l.lossQuantity),
      })),
    });
  }, [readyLines, conversionType, sourceWarehouseId]);

  useEffect(() => {
    if (!previewKey) { setPreview(null); return undefined; }
    setPreviewing(true);
    const timer = setTimeout(() => {
      materialConversionService.preview(JSON.parse(previewKey))
        .then(setPreview)
        .catch(() => setPreview(null))
        .finally(() => setPreviewing(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [previewKey]);

  const previewFor = (line: LineDraft) => {
    if (!preview) return null;
    const idx = readyLines.indexOf(line);
    return idx >= 0 ? preview.lines[idx] || null : null;
  };

  const blocked = (preview?.lines || []).filter((l) => !l.allowed);
  const overLoss = (preview?.lines || []).filter((l) => l.lossExceedsAllowance);
  const short = (preview?.lines || []).filter((l) => l.shortfall > 0);

  const canSave = readyLines.length > 0 && !!sourceWarehouseId
    && (!!reason.trim() || !!reasonCode)
    && blocked.length === 0 && overLoss.length === 0 && short.length === 0;

  const save = async () => {
    setError(null);
    if (!canSave) return;
    setSaving(true);
    try {
      const { conversion } = await materialConversionService.create({
        conversionDate,
        conversionType,
        sourceWarehouseId: Number(sourceWarehouseId),
        destWarehouseId: destWarehouseId ? Number(destWarehouseId) : Number(sourceWarehouseId),
        reasonCode: reasonCode || undefined,
        reason: reason.trim() || reasonCode,
        remarks: remarks.trim() || undefined,
        lines: readyLines.map((l) => ({
          sourceProductId: Number(l.sourceProduct.id),
          destProductId: Number(l.destProduct.id),
          sourceQuantity: toNum(l.sourceQuantity),
          lossQuantity: toNum(l.lossQuantity),
          sourceUom: l.sourceProduct.unit_of_measure || null,
          destUom: l.destProduct.unit_of_measure || null,
          remarks: l.remarks.trim() || undefined,
        })),
      });
      navigate(`/inventory/conversions/${conversion.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Failed to post the conversion');
      setSaving(false);
    }
  };

  if (loadingRef) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6 }}>
      <WorkspaceHeader
        title="Material Conversion"
        subtitle="Convert or reclassify stock as a real transaction — stock moves, cost follows, the ledger records it."
        onBack={() => navigate('/inventory/conversions')}
        actions={
          <Stack direction="row" spacing={1.25}>
            <Button variant="outlined" sx={outBtn} onClick={() => navigate('/inventory/conversions')} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained" sx={priBtn} disabled={!canSave || saving} onClick={save}
              startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SwapHoriz />}
            >
              Post Conversion
            </Button>
          </Stack>
        }
      />

      <Box sx={{ px: PX, pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {blocked.length > 0 && (
          <Alert severity="error" icon={<Lock />} sx={{ mb: 2 }}>
            {blocked.length === 1 ? 'One line is' : `${blocked.length} lines are`} not permitted by any configured
            conversion rule. Conversions are controlled so material cannot be turned into something else by
            accident — add a rule under Conversion Rules if the change is legitimate.
          </Alert>
        )}

        {/* --- header ---------------------------------------------------- */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>Conversion</Typography>
            <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>
              A conversion changes what the company owns, so a reason is required.
            </Typography>
          </Box>
          <Box sx={{ p: 2.5, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' } }}>
            <Box><FieldLabel text="Date" required />
              <TextField type="date" size="small" fullWidth value={conversionDate}
                onChange={(e) => setConversionDate(e.target.value)} sx={fieldSx} /></Box>
            <Box>
              <FieldLabel text="Type" required />
              <TextField select size="small" fullWidth value={conversionType}
                onChange={(e) => setConversionType(e.target.value as ConversionType)} sx={fieldSx}>
                {(Object.keys(CONVERSION_TYPE_LABELS) as ConversionType[]).map((t) => (
                  <MenuItem key={t} value={t}>{CONVERSION_TYPE_LABELS[t]}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Reason Code" />
              <TextField select size="small" fullWidth value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)} sx={fieldSx}>
                <MenuItem value="">Not set</MenuItem>
                {REASON_CODES.map((r) => <MenuItem key={r} value={r}>{r.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="From Warehouse" required />
              <TextField select size="small" fullWidth value={sourceWarehouseId}
                onChange={(e) => setSourceWarehouseId(e.target.value)} sx={fieldSx}>
                <MenuItem value="">Select…</MenuItem>
                {warehouses.map((w: any) => <MenuItem key={w.id} value={String(w.id)}>{w.warehouse_name}</MenuItem>)}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Into Warehouse" />
              <TextField select size="small" fullWidth value={destWarehouseId}
                onChange={(e) => setDestWarehouseId(e.target.value)} sx={fieldSx}>
                <MenuItem value="">Same as source</MenuItem>
                {warehouses.map((w: any) => <MenuItem key={w.id} value={String(w.id)}>{w.warehouse_name}</MenuItem>)}
              </TextField>
            </Box>
            <Box><FieldLabel text="Reason" required />
              <TextField size="small" fullWidth value={reason} placeholder="Why is this being converted?"
                onChange={(e) => setReason(e.target.value)} sx={fieldSx} /></Box>
          </Box>
        </Paper>

        {/* --- lines ------------------------------------------------------ */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: P.strong }}>What becomes what</Typography>
                {previewing && <CircularProgress size={13} thickness={5} sx={{ color: P.muted }} />}
              </Stack>
              <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>
                The output quantity and its cost are worked out for you from the rule's conversion factor
                and what the source actually cost.
              </Typography>
            </Box>
            <Button startIcon={<AddCircle />} onClick={addLine} sx={{ ...outBtn, height: 34 }}>Add line</Button>
          </Stack>
          <TableContainer>
            <Table sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, minWidth: 230 }}><Typography sx={thTitle}>From item *</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 110 }}><Typography sx={thTitle}>Quantity</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>In stock</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>Loss</Typography></TableCell>
                  <TableCell sx={{ ...thSx, minWidth: 230 }}><Typography sx={thTitle}>Into item *</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 110 }}><Typography sx={thTitle}>Output</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>New cost</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 150 }}><Typography sx={thTitle}>Rule</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l) => {
                  const pv = previewFor(l);
                  return (
                    <TableRow key={l.key} sx={{ verticalAlign: 'top', bgcolor: pv && !pv.allowed ? P.redBg : undefined }}>
                      <TableCell sx={{ py: 1.25 }}>
                        <Autocomplete
                          size="small" options={products} value={l.sourceProduct}
                          onChange={(_, v) => setLine(l.key, { sourceProduct: v })}
                          getOptionLabel={(p: any) => (p ? `${p.product_code} — ${p.product_name}` : '')}
                          isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                          renderInput={(params) => <TextField {...params} placeholder="Search…" sx={gridInput} />}
                        />
                        {l.sourceProduct && (
                          <Typography sx={{ fontSize: '11px', color: P.muted, mt: '3px' }}>
                            {String(l.sourceProduct.product_type || '').replace(/_/g, ' ').toLowerCase()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <TextField size="small" type="number" fullWidth value={l.sourceQuantity}
                          onChange={(e) => setLine(l.key, { sourceQuantity: e.target.value })}
                          error={!!pv && pv.shortfall > 0}
                          inputProps={{ style: { textAlign: 'right' } }} sx={gridInput} />
                      </TableCell>
                      <TableCell align="right" sx={{ pt: 2, fontSize: '13px', fontVariantNumeric: 'tabular-nums', color: pv && pv.shortfall > 0 ? P.red : P.text }}>
                        {pv ? formatIndianNumber(pv.availableStock) : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <TextField size="small" type="number" fullWidth value={l.lossQuantity}
                          onChange={(e) => setLine(l.key, { lossQuantity: e.target.value })}
                          error={!!pv && pv.lossExceedsAllowance}
                          helperText={pv && pv.lossExceedsAllowance ? `Max ${pv.allowedLossPct}%` : ' '}
                          inputProps={{ style: { textAlign: 'right' } }} sx={gridInput} />
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Autocomplete
                          size="small" options={products} value={l.destProduct}
                          onChange={(_, v) => setLine(l.key, { destProduct: v })}
                          getOptionLabel={(p: any) => (p ? `${p.product_code} — ${p.product_name}` : '')}
                          isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
                          renderInput={(params) => <TextField {...params} placeholder="Search…" sx={gridInput} />}
                        />
                        {l.destProduct && (
                          <Typography sx={{ fontSize: '11px', color: P.muted, mt: '3px' }}>
                            {String(l.destProduct.product_type || '').replace(/_/g, ' ').toLowerCase()}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Derived, never typed. */}
                      <TableCell align="right" sx={{ pt: 2, fontSize: '13.5px', fontWeight: 800, color: P.strong, fontVariantNumeric: 'tabular-nums' }}>
                        {pv ? formatIndianNumber(pv.destQuantity) : '—'}
                        {pv && pv.conversionFactor !== 1 && (
                          <Typography sx={{ fontSize: '10.5px', color: P.muted }}>× {pv.conversionFactor}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ pt: 2, fontSize: '13px', fontVariantNumeric: 'tabular-nums', color: P.text }}>
                        {pv ? `${formatIndianNumber(pv.destUnitCost)}` : '—'}
                      </TableCell>
                      <TableCell sx={{ pt: 1.75 }}>
                        {!pv ? (
                          <Typography sx={{ fontSize: '12px', color: P.muted }}>—</Typography>
                        ) : pv.allowed ? (
                          <Tooltip title={pv.ruleName || ''} arrow>
                            <Chip size="small" icon={<CheckCircle sx={{ fontSize: '14px !important' }} />}
                              label={pv.ruleCode}
                              sx={{ height: 21, fontSize: '11px', fontWeight: 700, bgcolor: P.greenBg, color: P.green, '& .MuiChip-icon': { color: P.green } }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={pv.reason || ''} arrow>
                            <Chip size="small" icon={<Lock sx={{ fontSize: '14px !important' }} />} label="Not allowed"
                              sx={{ height: 21, fontSize: '11px', fontWeight: 700, bgcolor: P.redBg, color: P.red, '& .MuiChip-icon': { color: P.red } }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ pt: 1.5 }}>
                        <IconButton size="small" disabled={lines.length <= 1} onClick={() => removeLine(l.key)}>
                          <DeleteOutline sx={{ fontSize: 18, color: lines.length <= 1 ? P.border : P.red }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ mb: 1.5 }}>
          <TotalsStrip cells={[
            { label: 'Lines', value: readyLines.length },
            { label: 'Value consumed', value: `${formatIndianNumber(preview?.totals.sourceValue || 0)}` },
            {
              label: 'Loss written off',
              value: `${formatIndianNumber(preview?.totals.lossValue || 0)}`,
              tone: (preview?.totals.lossValue || 0) > 0 ? 'red' : 'strong',
            },
            { label: 'Value received', value: `${formatIndianNumber(preview?.totals.destValue || 0)}`, tone: 'primary' },
          ]} />
        </Box>

        <ValidationStrip items={[
          { ok: !!sourceWarehouseId, label: sourceWarehouseId ? 'Source warehouse set' : 'Pick a source warehouse' },
          { ok: !!reason.trim() || !!reasonCode, label: (reason.trim() || reasonCode) ? 'Reason recorded' : 'A reason is required' },
          { ok: readyLines.length > 0, label: readyLines.length ? `${readyLines.length} line(s)` : 'Add a line' },
          { ok: blocked.length === 0, label: blocked.length ? `${blocked.length} line(s) not permitted by any rule` : 'Every line is authorised by a rule' },
          { ok: short.length === 0, label: short.length ? `${short.length} line(s) exceed stock on hand` : 'Stock available' },
          { ok: overLoss.length === 0, label: overLoss.length ? `${overLoss.length} line(s) exceed the allowed loss` : 'Loss within allowance' },
        ]} />
      </Box>
    </Box>
  );
};

export default MaterialConversionWorkspacePage;
